/**
 * <tt-faq-accordion> — animated FAQ accordion used by sections/tt-faq-accordion.liquid.
 *
 * Wraps a list of <details class="tt-faq__item"> elements and animates their
 * open/close with a height transition. Set `data-single-open="true"` on the
 * element to close the other items when one is opened.
 */
class TTFaqAccordion extends HTMLElement {
  connectedCallback() {
    // Keep in sync with the `transition-duration` on .tt-faq__item in
    // assets/tt-faq-accordion.css.
    this.transitionMs = 350;
    this.singleOpen = this.dataset.singleOpen === 'true';

    this.items = Array.from(this.querySelectorAll('.tt-faq__item'));
    this.items.forEach((item) => {
      const summary = item.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (item.classList.contains('is-animating')) return;

        if (item.hasAttribute('open')) {
          this.close(item);
        } else {
          if (this.singleOpen) {
            this.items.forEach((other) => {
              if (other !== item && other.hasAttribute('open')) this.close(other);
            });
          }
          this.open(item);
        }
      });
    });
  }

  open(item) {
    item.style.height = `${item.offsetHeight}px`;
    item.setAttribute('open', '');
    item.classList.add('is-animating');

    // Double rAF: paint a frame at the collapsed height before setting the
    // target height, otherwise the transition never fires.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        item.style.height = `${this.contentHeight(item)}px`;
      });
    });

    this.afterTransition(item, () => {
      item.style.height = '';
      item.classList.remove('is-animating');
    });
  }

  close(item) {
    item.style.height = `${item.offsetHeight}px`;
    item.classList.add('is-animating');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const summary = item.querySelector('summary');
        item.style.height = `${summary ? summary.offsetHeight : 0}px`;
      });
    });

    this.afterTransition(item, () => {
      item.removeAttribute('open');
      item.style.height = '';
      item.classList.remove('is-animating');
    });
  }

  contentHeight(item) {
    const summary = item.querySelector('summary');
    const content = item.querySelector('.tt-faq__answer');
    return (summary ? summary.offsetHeight : 0) + (content ? content.offsetHeight : 0);
  }

  afterTransition(item, cleanup) {
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      item.removeEventListener('transitionend', onEnd);
      clearTimeout(safetyTimer);
      cleanup();
    };

    const onEnd = (event) => {
      // Ignore transitions bubbling up from children.
      if (event.target !== item || event.propertyName !== 'height') return;
      finish();
    };

    item.addEventListener('transitionend', onEnd);
    // Fallback for when transitionend never fires (hidden tab, no height
    // change, detached node) so `is-animating` can't get stuck.
    const safetyTimer = setTimeout(finish, this.transitionMs + 150);
  }
}

if (!customElements.get('tt-faq-accordion')) {
  customElements.define('tt-faq-accordion', TTFaqAccordion);
}
